import { IdentityVerificationAction, IdentityVerificationMethod } from "../../../identity-verification/domain/models/identity-verification.model";

export enum AgreementSigningStatus {
  NotReady = "not-ready",
  ReadyToSign = "ready-to-sign",
  PendingCurrentUser = "pending-current-user",
  PendingOthers = "pending-others",
  Signed = "signed",
}

/** Which signing surface the portal must show. */
export enum SignatureMethod {
  Internal = "internal",
  DocuSign = "docusign",
}

export const SignatureMethodLabel = new Map<string, string>([
  [SignatureMethod.Internal, "In-house"],
  [SignatureMethod.DocuSign, "DocuSign"],
]);

/** Where one owner stands on the agreement. */
export enum AgreementSignerStatus {
  /** Something is still required of this owner — most often identity verification. */
  Pending = "pending",
  /** The agreement is ready and this owner can sign now. */
  ReadyToSign = "ready-to-sign",
  Signed = "signed",
}

/** One owner who must sign, as the dashboard's signer list names them. */
export interface AgreementSigner {
  customerId: number;
  fullName: string | null;
  /** True for the owner viewing the page, so the list can mark "you" without comparing names. */
  isCurrentUser: boolean;
  signingStatus: AgreementSignerStatus;
}

export interface AgreementLoanSigningErrors {
  balanceExceedsOffer: boolean;
  hasUnprocessedTransactions: boolean;
  isDisbursementBankEligible: boolean;
  isPaymentBankEligible: boolean;
}

export interface AgreementLoanState {
  entityId: number;
  isLoanVisibleForUser: boolean;
  agreementSigningStatus: AgreementSigningStatus;
  signatureMethod: SignatureMethod;
  /** Every owner who must sign, ascending by customer id — the order the agreement names them. */
  signers: AgreementSigner[];
  errors: AgreementLoanSigningErrors;
}

export interface AgreementApplicationState {
  entityId: number | null;
  applicationId: number;
  loanId: number | null;
  renewalForLoanId: number | null;
  agreementSigningStatus: AgreementSigningStatus;
  signatureMethod: SignatureMethod;
  /** Every owner who must sign, ascending by customer id — the order the agreement names them. */
  signers: AgreementSigner[];
  errors: AgreementApplicationSigningErrors;
}

export interface AgreementApplicationSigningErrors {
  isDisbursementBankEligible: boolean;
  isPaymentBankEligible: boolean;
}

export interface AgreementForLoanMeta {
  loanId: number;
  productCode: string;
  productName: string;
  agreementState: AgreementLoanState;
  renewalApplication?: AgreementApplicationState;
}

export interface NextAgreement {
  type: PendingAgreementType;
  id: number;
  entityId: number | null;
}

export interface SsoAccountData {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  preferredPhone: number;
  mobilePhone: number;
  mobilePhoneUnverified: number;
  waitingForPhoneConfirmation: boolean;
  loans: AgreementForLoanMeta[];
  pendingApplications: AgreementApplicationState[];
  nextAgreementToSign?: NextAgreement;
  referralCode: string;
  referredByCode: string;
  accountType: SsoAccountType;
}

export enum SsoAccountType {
  Internal = "internal",
  Google = "google",
  Apple = "apple",
}

export enum PendingAgreementType {
  Loan = 'loan',
  Application = 'application',
}

export class SsoAccountCheckoutState {
  errorMessage: string;
  method: IdentityVerificationMethod;
  identityVerificationAction: IdentityVerificationAction;
}
